import { useCallback } from 'react';
import { css } from '@emotion/react';
import dayjs, { Dayjs } from 'dayjs';
import { theme } from '@frontend/theme';
import {
  DateNavigator,
  CalendarDayType,
  FieldChangeEvent,
  Text,
  UseCalendarReturn,
  ResolvedFieldProps,
  calendarDayInRangeStyles,
} from '@frontend/design-system';
import { BOOKING_SITE_TRACKING_IDS } from '../../../../trackingIds/booking-site-tracking-ids';
import { getWeekRange, getWeekRangeText } from '../../../../utils/date-helpers';

type AvailabilityDateNavigatorProps = {
  weekInView: string;
  minDate: string;
  handleDateChange: (event?: FieldChangeEvent) => void;
  dateFieldProps: ResolvedFieldProps<'datePicker'>;
  isSchedularV3Enabled: boolean;
  selectedLocationTimeZone: string;
};

export const AvailabilityDateNavigator = ({
  weekInView,
  minDate,
  handleDateChange,
  dateFieldProps,
  isSchedularV3Enabled,
  selectedLocationTimeZone,
}: AvailabilityDateNavigatorProps) => {
  const timezoneArg = isSchedularV3Enabled ? selectedLocationTimeZone : undefined;

  const modifyCalendarProps = useCallback(
    (props: UseCalendarReturn) => {
      const { startOfWeek: startOfRange, endOfWeek: endOfRange } = getWeekRange(weekInView);
      const rangeStart = dayjs(startOfRange.toISOString());
      const rangeEnd = dayjs(endOfRange.toISOString());
      const isRangeValid = rangeStart.isBefore(rangeEnd);
      let rangeNumber = 1;
      let prevWeekEnd: Dayjs | null = null;
      const rangeNumberDateMap: Record<string, string> = {};

      const getDayButtonProps: UseCalendarReturn['getDayButtonProps'] = (day: CalendarDayType) => {
        const dayButtonProps = props.getDayButtonProps(day);
        const dayObject = dayjs(day.date);
        const dayJsObj = dayjs(dayObject.toISOString());
        const { startOfWeek, endOfWeek } = getWeekRange(day.date);
        const weekStart = dayjs(startOfWeek.toISOString());
        const weekEnd = dayjs(endOfWeek.toISOString());
        const dataAttrs: Record<string, string> = {};
        dataAttrs['in-range'] = (
          isRangeValid ? dayJsObj.isBetween(rangeStart, rangeEnd, 'day', '[]') : false
        ).toString();
        dataAttrs['range-start'] = dayJsObj.isSame(rangeStart).toString();
        dataAttrs['range-end'] = dayJsObj.endOf('day').isSame(rangeEnd).toString();
        dataAttrs['hover-range-start'] = dayJsObj.isSame(weekStart).toString();
        dataAttrs['hover-range-end'] = dayJsObj.endOf('day').isSame(weekEnd).toString();
        dataAttrs['in-hover-range'] = dayJsObj.isBetween(weekStart, weekEnd, 'day', '[]').toString();

        dataAttrs['range-number'] = rangeNumberDateMap[day.date] ?? rangeNumber.toString();
        if (!rangeNumberDateMap[day.date]) {
          rangeNumberDateMap[day.date] = rangeNumber.toString();
        }

        if (weekEnd.isSame(dayJsObj.endOf('day')) && !weekEnd.isSame(prevWeekEnd)) {
          prevWeekEnd = weekEnd;
          rangeNumber++;
        }

        return {
          ...dayButtonProps,
          ...dataAttrs,
          css: css`
            div.cal__month:has(&:not([aria-disabled='true']):hover) {
              & [range-number='${dataAttrs['range-number']}'][in-hover-range='true']:not([in-range='true']) {
                --in-range-background: ${theme.colors.neutral5};
                ${calendarDayInRangeStyles({
                  rangeStartSelector: "[hover-range-start='true']",
                  rangeEndSelector: "[hover-range-end='true']",
                })}
              }
            }
          `,
        };
      };

      return { ...props, getDayButtonProps };
    },
    [weekInView]
  );

  return (
    <DateNavigator
      {...dateFieldProps}
      modifyCalendarProps={modifyCalendarProps}
      variant='button'
      today={minDate} // To start calendar picker from minDate, this will reduce user navigation in case lead time is too far in future
      minDate={minDate}
      hideNavigationButtons
      label=''
      name='week-anchor-date'
      onChange={handleDateChange}
      trackingIds={{
        dateInput: BOOKING_SITE_TRACKING_IDS.providerStep.weekAnchorDateInput,
        datePicker: BOOKING_SITE_TRACKING_IDS.providerStep.weekAnchorDatePicker,
      }}
    >
      <Text weight='regular'>{getWeekRangeText(weekInView, timezoneArg)}</Text>
    </DateNavigator>
  );
};
